import dynamic from 'next/dynamic';
import { ReactElement, SyntheticEvent } from 'react';
import { useDispatch } from 'react-redux';

import { useBooleanFlags, useFlags } from '@/core/flags/flags.hooks';
import { Image } from '@/domains/core/Image/Image';
import {
  getCurrency,
  isB2B,
  isFrancePlatform,
  isGermanyPlatform,
} from '@/domains/core/settings/utils';
import { CurrencyType } from '@/domains/core/tamagoshi/Price/Price.interfaces';
import { useTrackViewedElement } from '@/domains/core/tracking/hooks/useTrackViewedElement';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import {
  generateUrl,
  mapTaxToProductCardTax,
} from '@/domains/productDiscovery/algolia/components/AlgoliaProductCard/AlgoliaProductCard.utils';
import type { ProductCardClickTracking } from '@/domains/productDiscovery/algolia/components/AlgoliaProductCard/interfaces/clickTracking';
import type { Product } from '@/domains/productDiscovery/algolia/interfaces/product';
import { getTaxKey } from '@/domains/productDiscovery/algolia/productProcessor';
import { calculateCouponAmount } from '@/domains/productDiscovery/CommercialAnimation/utils/calculateCouponAmount';
import { productHasDiscount } from '@/domains/productDiscovery/CommercialAnimation/utils/getProductPromoTags';
import { getDelivery } from '@/domains/productDiscovery/ProductCard/utils/getDelivery';
import type { AddToCartPayload } from '@/domains/shoppingCart/AddToCart/AddToCart.actions';
import type { Product as ProductCart } from '@/domains/shoppingCart/AddToCart/AddToCart.interfaces';
import { CROSS_SELL_A2C_PRODUCTS_KEY } from '@/domains/shoppingCart/CartPage/components/CartByDispatches/contants';
import { displayAdvertiserInformation } from '@/productDiscovery/CommercialAnimation/CommercialAnimation.actions';
import { createAdvertiserInformationPopupContent } from '@/productDiscovery/CommercialAnimation/utils/createAdvertiserInformationPopupContent';
import { productLabels } from '@/productDiscovery/Listings/components/ProductCardListingsEnhancer/components/ProductCardListings/interfaces';
import {
  ProductCardListings,
  type ProductCardListingsProps,
} from '@/productDiscovery/Listings/components/ProductCardListingsEnhancer/components/ProductCardListings/ProductCardListings';
import {
  StringFeatureFlags as FeatureFlags,
  StringABTests,
} from '@/productDiscovery/Listings/constants';

import { ListingInspectorWrapperProps } from '../../developerTools/listingsInspector/ListingInspectorWrapper';
import { useInspectListings } from '../../hooks/useInspectListings';
import { ProductCardLink } from './components/Link/Link';
import {
  pickMiddleLabels,
  pickThirdLineLabels,
  pickTopLeftLabel,
} from './components/ProductCardListings/utils/productLabels';
import { getProductCardTranslations } from './translations';

import styles from './ProductCardListingsEnhancer.module.scss';

const DynamicListingInspectorWrapper = dynamic<ListingInspectorWrapperProps>(
  () =>
    import(
      '@/productDiscovery/Listings/developerTools/listingsInspector/ListingInspectorWrapper'
    ).then((mod) => mod.ListingInspectorWrapper),
);

export interface ProductCardListingsEnhancerTyping {
  <P extends Product = Product>(
    props: ProductCardListingsEnhancerProps<P>,
  ): ReactElement<any, any> | null;
}

export type ProductCardListingsEnhancerProps<P extends Product = Product> =
  Omit<
    ProductCardListingsProps,
    | 'translations'
    | 'currency'
    | 'imageComponent'
    | 'tag'
    | 'onClick'
    | 'b2b'
    | 'tagProps'
    | 'rating'
    | 'ratingCount'
    | 'legalMention'
    | 'legalMentionNoVAT'
    | 'brandImageSrc'
    | 'brandName'
    | 'modelsCount'
    | 'title'
    | 'alternateTitle'
    | 'imageSrc'
    | 'price'
    | 'secondaryPrice'
    | 'retailPrice'
    | 'pro'
    | 'delivery'
    | 'onAddToCart'
    | 'madeIn'
    | 'ecoResponsible'
    | 'tax'
    | 'secondaryUnit'
    | 'imageSrcSet'
  > & {
    product: P;
    promoted?: boolean;
    trackingListName?: string;
    position: number;
    displayAddToCartCustomSummary?: boolean;
    displayAddToCartModal?: boolean;
    onClick?: (
      event: React.MouseEvent<HTMLElement, MouseEvent>,
      product: P,
      position: number,
      defaultTracking: ProductCardClickTracking,
    ) => void;
    onViewed?(Product: P): void;
    onAddToCart?: (payload: AddToCartPayload) => void;
    favourite?: boolean;
    adPayer?: 'BRAND' | 'SELLER';
    isSponsored?: boolean;
    proPriceAdvantageLabel?: boolean;
  };

export const ProductCardListingsEnhancer = <P extends Product = Product>({
  imageLoading,
  provideImageDataSrc,
  trackingListName,
  className,
  product,
  promoted = false,
  position,
  displayAddToCartCustomSummary = false,
  onClick,
  onAddToCart,
  onViewed,
  favourite,
  adPayer,
  isSponsored = false,
  proPriceAdvantageLabel = false,
  ...otherProps
}: ProductCardListingsEnhancerProps<P>) => {
  const [bulkLabelEnabled, technicalInfoEnabled] = useBooleanFlags([
    FeatureFlags.BULK_LABEL,
    StringABTests.TECHNICAL_INFO_IN_PC,
  ]);
  const [alternateTitle] = useFlags([StringABTests.ALTERNATE_TITLE_IN_PC], {
    flagTypeToCheck: 'stringOrUndefined',
  });
  const alternateTitleEnabled = Boolean(alternateTitle);

  const [ref] = useTrackViewedElement(
    () => {
      if (onViewed) {
        onViewed(product);
      }
    },
    { skip: !onViewed },
  );

  const hasAlternateTitle = alternateTitleEnabled && isFrancePlatform();
  const productTitle =
    hasAlternateTitle && !!product.alternateTitle
      ? product.alternateTitle
      : product.title;

  const trackProductCard = async (event: React.MouseEvent<HTMLElement>) => {
    const { computeDefaultTrackingData } = await import(
      '@/domains/productDiscovery/algolia/components/AlgoliaProductCard/tracking'
    );
    const trackingData = computeDefaultTrackingData({
      listName: trackingListName,
      position,
      product,
      price: product.prices.main,
      isSponsored,
      deliveryMode: getDelivery(product),
    });

    if (onClick) {
      onClick(event, product, position, trackingData);
    } else {
      Gtm.push(trackingData);
    }
  };

  const dispatch = useDispatch();
  const handleAddToCart = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const { SafeStorage } = await import(
      '@/domains/core/data/utils/SafeStorage/SafeStorage'
    );

    const productId = parseInt(product.productId, 10);
    const safeStorage = SafeStorage.getLocalStorage();
    const savedProductsIds = safeStorage.getItem(CROSS_SELL_A2C_PRODUCTS_KEY);

    safeStorage.setItem(
      CROSS_SELL_A2C_PRODUCTS_KEY,
      savedProductsIds ? `${savedProductsIds},${productId}` : productId,
    );

    const productCart: ProductCart = {
      name: productTitle,
      picture: product.imageFullpath || '',
      id: productId,
      quantity: 1,
      stockSize: 1, // TODO: À voir ou récupérer l'info
      sellerName: product.sellerName,
      brandName: product.brandName,
      isDeliveredByManomano: product.isMmf,
      b2b: product.isSellerB2b,
      unitPrice: parseInt('', 10), // because it was already null value
      reference: product.productId,
      oldUnitPrice: parseInt('', 10), // because it was already null value
      prices: product.prices,
      pr_model_id: String(product.modelId),
      pr_position: position + 1,
    };

    if (onAddToCart) {
      if (isB2B()) {
        event.stopPropagation();
      }
      onAddToCart({
        products: [productCart],
        displayAddToCartCustomSummary,
      });
    } else {
      const { addToCart } = await import(
        '@/domains/shoppingCart/AddToCart/AddToCart.actions'
      );
      dispatch(
        addToCart({
          products: [productCart],
          displayAddToCartCustomSummary,
        }),
      );
    }
  };

  const topLeftLabel = pickTopLeftLabel({
    product,
    bulkLabelEnabled,
    proPriceAdvantageLabelEnabled: proPriceAdvantageLabel,
  });

  const middleLabels = pickMiddleLabels({
    product,
    isSponsored,
    technicalInfoEnabled,
  });

  const thirdLineLabels = pickThirdLineLabels({
    product,
    technicalInfoEnabled,
  });

  const isProductInPromo =
    product.commercialAnimation?.tags?.includes(productLabels.PROMO) &&
    productHasDiscount(product.prices.main, product.prices.retail);

  const inspectListings = useInspectListings();
  const productCardKey = `${product.productId}${product.fromCustomQuery ? '-sponsored' : ''}`;
  const sponsoredData = {
    adPayer,
    sellerName: product.sellerName,
    brandName: product.brandName,
  };
  const translations = getProductCardTranslations(
    product.modelsCount,
    calculateCouponAmount(product.coupon),
    isFrancePlatform(),
    sponsoredData,
    product.proAdvantagePercentage,
  );
  const onAdvertiserLabelClicked = async (e: SyntheticEvent) => {
    e.preventDefault();
    const adInfo = {
      sponsor: sponsoredData.sellerName,
      advertiser: sponsoredData.brandName,
      userSegments: true,
      dsaPopup: !!(sponsoredData.sellerName || sponsoredData.brandName),
      content: createAdvertiserInformationPopupContent(
        sponsoredData.brandName,
        sponsoredData.sellerName,
        true,
      ),
    };
    dispatch(displayAdvertiserInformation(adInfo));
  };

  const ProductCard = (
    <ProductCardListings
      {...otherProps}
      ref={ref}
      className={className}
      currency={getCurrency() as CurrencyType}
      translations={translations}
      imageComponent={Image as ProductCardListingsProps['imageComponent']}
      imageLoading={imageLoading}
      b2b={isB2B()}
      tag={ProductCardLink}
      tagProps={{
        url: generateUrl(product, promoted, isSponsored),
        favourite,
      }}
      rating={product.rating}
      ratingCount={product.ratingCount}
      legalMention={isGermanyPlatform()}
      legalMentionNoVAT={product.prices.hasNoVATForDE}
      brandImageSrc={product.brandImageFullpath}
      brandName={product.brandName}
      modelsCount={product.modelsCount}
      title={productTitle}
      imageSrc={product.imageFullpath || ''}
      imageSrcSet={`${product.imageFullpath?.replace('-P-', '-T-')} 1x, ${
        product.imageFullpath
      } 2x`}
      provideImageDataSrc
      price={product.prices.main}
      secondaryPrice={product.prices.secondary || undefined}
      retailPrice={product.prices.retail || undefined}
      pro={product.hasProConditions}
      delivery={getDelivery(product)}
      onClick={trackProductCard}
      onAddToCart={handleAddToCart}
      madeIn={product.isLocal ? 'fr' : undefined}
      ecoResponsible={!!product.isEcoResponsible}
      tax={isB2B() ? mapTaxToProductCardTax(getTaxKey()) : undefined}
      secondaryUnit={product.prices.unit || undefined}
      popoverContentClassName={styles.popoverContent}
      topLeftLabel={topLeftLabel}
      middleLabels={middleLabels}
      technicalInfoEnabled={technicalInfoEnabled}
      thirdLineLabels={thirdLineLabels}
      promoted={promoted}
      promo={isProductInPromo}
      onAdvertiserLabelClicked={onAdvertiserLabelClicked}
    />
  );
  return inspectListings ? (
    <DynamicListingInspectorWrapper
      key={productCardKey}
      isSponsored={isSponsored}
      product={product}
    >
      {ProductCard}
    </DynamicListingInspectorWrapper>
  ) : (
    ProductCard
  );
};

ProductCardListingsEnhancer.displayName = 'ProductCardListingsEnhancer';
